import { useCallback, useMemo, useState } from 'react'

import { Flex } from 'antd'
import { MultiSelect, Select } from 'govwell-ui'
import { SelectModeProps, SelectBaseProps, SelectOption } from 'govwell-ui/components/Select/types'
import { runInAction } from 'mobx'
import { FeeFragment, FeeType } from 'types/graphql'

import Text, { TextSize } from 'src/components/Typography/Text'
import { useFeesQuery } from 'src/fetch/fees'
import { filterNil } from 'src/utils'

import { DefaultAllowedFeeTypes } from '../../utils/fee'

const getOptionFromFeeTemplate = (ft: FeeFragment): SelectOption<number> => {
  return {
    label: `${ft.name}${ft.isArchived ? ' (Archived)' : ''}`,
    customDisplay: () => (
      <Flex vertical>
        <Text>
          {ft.name}
          {ft.isArchived ? ' (Archived)' : ''}
        </Text>
        <Text colorToken="colorTextSecondary" size={TextSize.Small}>
          {ft.description}
        </Text>
      </Flex>
    ),
    value: ft.id,
  }
}

type Props = {
  allowedTypes?: FeeType[]
  caption?: React.ReactNode
  isClearable?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  width?: React.CSSProperties['width']
  isDisabled?: boolean
  placeholder?: string
  disabledFeeIds?: number[]
  feeScheduleId?: number
} & SelectModeProps<number>

const FeeTemplateSelect = ({
  allowedTypes = DefaultAllowedFeeTypes,
  caption,
  isClearable,
  isRequired,
  label,
  onValueChange,
  value,
  width,
  isMultiSelect,
  isDisabled,
  placeholder = 'Select a fee template',
  disabledFeeIds,
  feeScheduleId,
}: Props) => {
  const valueAsArray = useMemo(() => (Array.isArray(value) ? value : filterNil([value])), [value])

  // Explicitly load the initial set of fee ids to include any that may have been archived
  const [includeFeeIds] = useState<number[]>(valueAsArray)
  const { feeTemplates, isLoading: isLoadingOptions } = useFeesQuery({
    allowedTypes,
    includeFeeIds,
    feeScheduleId,
  })

  const options: SelectOption<number>[] = useMemo(
    () =>
      feeTemplates
        ?.filter((ft) => !(disabledFeeIds || []).includes(ft.id))
        ?.map(getOptionFromFeeTemplate) ?? [],
    [feeTemplates, disabledFeeIds]
  )

  const selectedOptionSingleSelect = useMemo(() => {
    if (!isMultiSelect) {
      return options.find((o) => o.value === value)
    }
  }, [isMultiSelect, options, value])

  const selectedOptions = useMemo(
    () => options.filter((o) => valueAsArray.includes(o.value)),
    [options, valueAsArray]
  )

  const handleSelectedOptionsChange = useCallback(
    (_options: SelectOption<number>[]) => {
      if (isMultiSelect) {
        onValueChange(_options.map((o) => o.value))
      } else {
        onValueChange(_options[0]?.value ?? null)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<number> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(option?.value ?? null)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const getOptionKey = useCallback((option: SelectOption<number>) => {
    return runInAction(() => option.value)
  }, [])

  const selectBaseProps: SelectBaseProps<number> = {
    caption,
    getOptionKey,
    isClearable,
    isLoading: isLoadingOptions,
    isRequired,
    label,
    options,
    placeholder,
    width,
    isDisabled,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        {...selectBaseProps}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        selectedOptions={selectedOptions}
      />
    )
  }

  return (
    <Select
      {...selectBaseProps}
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOptionSingleSelect}
    />
  )
}

export default React.memo(FeeTemplateSelect)
